import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, Loader2, ArrowUpRight, LogOut, FileText, Download, Users, Briefcase } from "lucide-react";
import Seo from "../components/site/Seo";
import { SectionLabel } from "../components/site/Section";
import { Reveal } from "../components/site/Reveal";
import {
  clientLogin, clientMe, clientRequisitions, clientDocuments, clientDownloadDoc,
  clientChangePassword, getClientToken, setClientToken, apiError,
} from "../lib/api";
import SetPasswordDialog from "../components/site/SetPasswordDialog";
import ForgotPasswordDialog from "../components/site/ForgotPasswordDialog";

const FIELD = "w-full border border-border bg-secondary px-4 py-3 text-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-brand";
const REQ_LABEL = { open: "Open", on_hold: "On Hold", filled: "Filled", closed: "Closed" };
const REQ_COLOR = { open: "text-brand", on_hold: "text-yellow-500", filled: "text-green-500", closed: "text-muted-foreground" };
const SUB_COLOR = { submitted: "text-muted-foreground", interviewing: "text-blue-400", offer: "text-yellow-500", hired: "text-green-500", declined: "text-red-400" };

export default function ClientPortal() {
  const [user, setUser] = useState(null);
  useEffect(() => {
    if (!getClientToken()) return setUser(false);
    clientMe().then(setUser).catch(() => { setClientToken(null); setUser(false); });
  }, []);
  if (user === null) return <div className="grid min-h-screen place-items-center bg-background"><Loader2 className="animate-spin text-brand" /></div>;
  return user ? <Dashboard user={user} onLogout={() => { setClientToken(null); setUser(false); }} /> : <ClientLogin onAuthed={setUser} />;
}

function ClientLogin({ onAuthed }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const change = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return toast.error("Enter your email and password.");
    setLoading(true);
    try {
      const data = await clientLogin(form.email, form.password);
      if (data.user.role !== "client") { setClientToken(null); return toast.error("This portal is for clients. Please use the correct login."); }
      toast.success("Welcome back.");
      onAuthed(data.user);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Login failed.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="client-portal-page">
      <Seo title="Client Portal" description="Secure client portal for VectorBridge Group — track requisitions, candidate submittals, and program documents." />
      <section className="relative min-h-[100svh] pt-32">
        <div className="tech-grid pointer-events-none absolute inset-0 opacity-20 mask-fade-b" />
        <div className="relative mx-auto grid max-w-[1400px] items-center gap-16 px-5 py-16 sm:px-8 lg:grid-cols-2">
          <div>
            <SectionLabel index="P/01">Client Portal</SectionLabel>
            <Reveal><h1 className="mt-8 font-display text-5xl font-light leading-[0.95] tracking-tighter sm:text-6xl">Your engagement, <span className="text-brand">in real time.</span></h1></Reveal>
            <Reveal delay={0.1}><p className="mt-6 max-w-md text-muted-foreground">Log in to review your open requisitions, track candidate submittals through the pipeline, and access program reporting.</p></Reveal>
            <ul className="mt-10 divide-y divide-border border-t border-border">
              {["View active requisitions & fill status", "Track candidate submittals in real time", "Download reports & program documents"].map((b, i) => (
                <Reveal key={b} delay={0.1 + i * 0.05}><li className="flex items-center gap-3 py-4 text-sm text-muted-foreground"><ShieldCheck size={16} className="text-brand" /> {b}</li></Reveal>
              ))}
            </ul>
          </div>
          <Reveal delay={0.15}>
            <div className="border border-border bg-card/60 p-6 backdrop-blur-xl sm:p-10">
              <div className="flex items-center gap-2 text-brand"><Lock size={16} /> <span className="font-mono text-[11px] uppercase tracking-[0.2em]">Encrypted Access</span></div>
              <form onSubmit={submit} data-testid="client-login-form" className="mt-6 space-y-4">
                <input name="email" type="email" value={form.email} onChange={change} placeholder="Work email" className={FIELD} data-testid="client-email" />
                <input name="password" type="password" value={form.password} onChange={change} placeholder="Password" className={FIELD} data-testid="client-password" />
                <button type="submit" disabled={loading} data-testid="client-login-submit" className="inline-flex w-full items-center justify-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />} Enter portal
                </button>
              </form>
              <p className="mt-6 text-center text-xs text-muted-foreground">Need access? <Link to="/contact" className="text-brand link-underline">Contact your account team</Link></p>
              <button type="button" onClick={() => setForgot(true)} data-testid="client-forgot-password" className="mt-4 block w-full text-center font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-brand">Forgot password?</button>
              <ForgotPasswordDialog open={forgot} onOpenChange={setForgot} />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [reqs, setReqs] = useState(null);
  const [docs, setDocs] = useState(null);
  const [mustChange, setMustChange] = useState(!!user.must_change_password);
  useEffect(() => {
    clientRequisitions().then(setReqs).catch(() => setReqs([]));
    clientDocuments().then(setDocs).catch(() => setDocs([]));
  }, []);

  return (
    <div data-testid="client-dashboard" className="min-h-screen bg-background">
      <SetPasswordDialog open={mustChange} onSubmit={async (pw) => { await clientChangePassword(pw); setMustChange(false); }} />
      <header className="border-b border-border pt-28 pb-10">
        <div className="mx-auto flex max-w-[1200px] flex-wrap items-end justify-between gap-4 px-5 sm:px-8">
          <div>
            <SectionLabel index="P/01">Client Portal</SectionLabel>
            <h1 className="mt-6 font-display text-4xl font-light tracking-tighter sm:text-5xl">{user.company || user.name || "Welcome"}.</h1>
            <p className="mt-2 text-sm text-muted-foreground">{user.email}</p>
          </div>
          <button onClick={onLogout} data-testid="client-logout" className="inline-flex items-center gap-2 border border-border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-foreground"><LogOut size={14} /> Sign out</button>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-5 py-12 sm:px-8">
        <div className="flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground"><span className="h-px w-8 bg-brand" /> Active Requisitions</div>
        {reqs === null ? (
          <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : reqs.length === 0 ? (
          <div className="mt-8 border border-dashed border-border p-12 text-center"><Briefcase size={30} className="mx-auto text-muted-foreground" /><p className="mt-4 font-display text-2xl tracking-tight">No active requisitions.</p><p className="mt-2 text-muted-foreground">New job orders from your account team will appear here.</p></div>
        ) : (
          <div className="mt-8 space-y-4">
            {reqs.map((r) => (
              <div key={r.id} data-testid={`client-req-${r.id}`} className="border border-border bg-card/40 p-6 sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display text-2xl tracking-tight">{r.title}</h3>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-muted-foreground">
                      {r.location && <span>{r.location}</span>}
                      <span>{r.openings} opening{r.openings === 1 ? "" : "s"}</span>
                    </div>
                  </div>
                  <span className={`font-mono text-[10px] uppercase tracking-[0.15em] ${REQ_COLOR[r.status] || "text-muted-foreground"}`}>{REQ_LABEL[r.status] || r.status}</span>
                </div>
                {r.notes && <p className="mt-3 text-sm text-muted-foreground">{r.notes}</p>}
                <div className="mt-5 border-t border-border pt-4">
                  <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground"><Users size={13} /> Submittals ({r.submittals?.length || 0})</div>
                  {r.submittals?.length ? (
                    <div className="mt-3 divide-y divide-border">
                      {r.submittals.map((s) => (
                        <div key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm">
                          <div><span className="text-foreground">{s.candidateName}</span>{s.role && <span className="text-muted-foreground"> · {s.role}</span>}{s.note && <span className="text-muted-foreground/70"> — {s.note}</span>}</div>
                          <span className={`font-mono text-[10px] uppercase tracking-[0.12em] ${SUB_COLOR[s.status] || "text-muted-foreground"}`}>{s.status}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="mt-2 text-sm text-muted-foreground/70">No candidates submitted yet.</p>}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-14 flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.25em] text-muted-foreground"><span className="h-px w-8 bg-brand" /> Documents & Reports</div>
        {docs === null ? (
          <div className="mt-8 flex justify-center"><Loader2 className="animate-spin text-brand" /></div>
        ) : docs.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground">No documents shared yet.</p>
        ) : (
          <div className="mt-6 space-y-2">
            {docs.map((d) => (
              <div key={d.id} data-testid={`client-doc-${d.id}`} className="flex items-center justify-between gap-3 border border-border px-4 py-4">
                <div className="flex items-center gap-3"><FileText size={18} className="text-brand" /><span>{d.title}</span></div>
                <button onClick={() => clientDownloadDoc(d.id, d.file?.original_filename)} data-testid={`client-doc-dl-${d.id}`} className="inline-flex items-center gap-2 border border-border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors hover:border-brand hover:text-brand"><Download size={14} /> Download</button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
